import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  opsApi, ApiRequestError,
  type InterviewMode, type ScreeningResult, type VettingQueueItem, type Worker360,
} from '@pinchcrew/core'
import { t } from '../i18n'

export const useVettingStore = defineStore('ops-vetting', () => {
  const queue = ref<VettingQueueItem[]>([])
  const current = ref<Worker360 | null>(null)
  const loading = ref(false)
  const busy = ref(false)
  const error = ref<string | null>(null)
  const stageFilter = ref<string>('')
  const agingOnly = ref(false)

  function fail(e: unknown): false {
    error.value = e instanceof ApiRequestError ? e.message : t('common.somethingWrong')
    return false
  }

  async function loadQueue() {
    loading.value = true
    error.value = null
    try {
      const page = await opsApi.getVettingQueue({
        stage: stageFilter.value || undefined,
        min_waiting_hours: agingOnly.value ? 48 : undefined,
      })
      queue.value = page.results
    } catch (e) {
      fail(e)
    } finally {
      loading.value = false
    }
  }

  async function open(workerId: string) {
    loading.value = true
    error.value = null
    screening.value = null
    try {
      current.value = await opsApi.getWorker360(workerId)
      // Fetched here so the suggestion is on screen when the reviewer
      // decides, not behind an extra click.
      await loadScreening(workerId)
    } catch (e) {
      fail(e)
    } finally {
      loading.value = false
    }
  }

  /** Wraps a mutation: clears the error, runs it, refreshes the 360 view. */
  async function act<T>(fn: () => Promise<T>, refresh = true): Promise<T | null> {
    busy.value = true
    error.value = null
    try {
      const result = await fn()
      if (refresh && current.value) current.value = await opsApi.getWorker360(current.value.worker.id)
      return result
    } catch (e) {
      fail(e)
      return null
    } finally {
      busy.value = false
    }
  }

  /**
   * Deliberately NOT wrapped in act(): the viewer shows a load failure in
   * place, next to the document it belongs to, rather than as a page-level
   * banner. So the error is allowed to propagate to the caller.
   */
  const documentFile = (id: string) => opsApi.getDocumentFile(id)

  /**
   * The AI's suggestion for the worker currently open.
   *
   * Loaded alongside the 360 view rather than on demand: a reviewer deciding
   * a tier should see the suggestion without having to go looking for it.
   * A failure here must not blank the page, so it degrades to null and the
   * UI says screening is unavailable.
   */
  const screening = ref<ScreeningResult | null>(null)

  async function loadScreening(workerId: string) {
    try {
      screening.value = await opsApi.getScreening(workerId)
    } catch {
      screening.value = null
    }
  }

  const verifyDocument = (id: string) => act(() => opsApi.verifyDocument(id))
  const rejectDocument = (id: string, reason: string) => act(() => opsApi.rejectDocument(id, reason))
  const passStage = (workerId: string, slug: string, note?: string) =>
    act(() => opsApi.passStage(workerId, slug, note))
  const failStage = (workerId: string, slug: string, reason: string) =>
    act(() => opsApi.failStage(workerId, slug, reason))
  const scheduleInterview = (workerId: string, at: string, mode: InterviewMode) =>
    act(() => opsApi.scheduleInterview(workerId, at, mode))
  const recordInterview = (id: string, outcome: 'passed' | 'failed' | 'no_show', notes?: string) =>
    act(() => opsApi.recordInterview(id, outcome, notes))
  const suspendWorker = (workerId: string, reason: string, days: number) =>
    act(() => opsApi.suspendWorker(workerId, reason, days))
  const waiveSuspension = (id: string, reason: string) => act(() => opsApi.waiveSuspension(id, reason))

  async function activate(workerId: string) {
    const result = await act(() => opsApi.activateWorker(workerId))
    if (result) await loadQueue()
    return result
  }

  function clearError() { error.value = null }

  return {
    queue, current, loading, busy, error, stageFilter, agingOnly,
    screening,
    loadQueue, open, documentFile, loadScreening, verifyDocument, rejectDocument, passStage, failStage,
    scheduleInterview, recordInterview, activate, suspendWorker, waiveSuspension, clearError,
  }
})
